import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';
import { Grid, Button } from '@material-ui/core';
import { paymentMode } from '@ourbranch/lookups';

import { PolicyStatus } from 'core/helpers/policy-status';
import { useDisabledState } from 'common/disabled-context';
import { useBasisTheoryToken } from 'common/hooks/use-basis-theory-token';
import { FormField } from 'core/components/form';
import { LabelTooltip } from 'core/components/label-tooltip';
import { useStore } from 'core/store';
import { useToast } from 'core/components/toast';
import addCard from 'core/assets/svg/add-card.svg';
import useSession from 'core/hooks/use-session';
import { CreditCardForm } from '../../../payment-tab/credit-card-form';
import { maskingDots } from '../helpers';
import { StripeCustomerIdField } from '../stripe-customer-id-field';
import SendCreditCardLinksModal from '../send-credit-card-links-modal';
import { SecondaryPaymentMethod } from '../secondary-payment-method';
import { PolicyTabs } from 'core/store/account-store/policies-store/policy-store/policy-store';
import useStyles from '../billing-details.styles';

const CreditCards = ({ ccModalPage }) => {
  const classes = useStyles();
  const { isAgency, viewOnly } = useSession();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const toast = useToast();
  const {
    account: {
      id: accountId,
      policies: {
        policy: {
          policy: { id },
          addCreditCard,
          billingDetails,
          isCancelledOrInCancellation,
          status,
          setCurrentTab
        }
      }
    }
  } = useStore();
  const { getBasisTheoryCCToken } = useBasisTheoryToken();
  const [showAdd, setShowAdd] = useState(false);
  const [openCreditCardModal, setOpenCreditCardModal] = useState(false);
  const { setDisabled } = useDisabledState();

  const allPaymentMethods = billingDetails.allPaymentMethods || [];

  const cancelled = status === PolicyStatus.Cancelled;

  useEffect(() => {
    if (values.paymentMode !== paymentMode.Automated) {
      setFieldValue('paymentMode', paymentMode.Automated);
    }
  }, [values?.paymentMode, setFieldValue]);

  const handleAddCard = async () => {
    try {
      const { token } = await getBasisTheoryCCToken();
      const {
        success,
        message,
        card: cardDetails
      } = await addCreditCard({
        accountId,
        policyId: id,
        basisTheoryCardToken: token,
        customerId: values.stripeCustomerId,
        isPrimary: true
      });
      const externalAgentLabelText =
        isCancelledOrInCancellation && isAgency
          ? 'To process payments, contact support.'
          : 'To process payments, go to payment and price tab.';
      toast.notify({
        type: success ? 'success' : 'error',
        message,
        label: externalAgentLabelText,
        action: () => setCurrentTab(PolicyTabs.PAYMENT),
        durationInSeconds: 10
      });
      setShowAdd(false);
      if (success && !isCancelledOrInCancellation) {
        setFieldValue('defaultCreditCard', {
          id: cardDetails.id,
          value: `${cardDetails.brand ? cardDetails.brand : ''} ${maskingDots}${cardDetails.last4}`
        });
        setFieldTouched('defaultCreditCard', true);
        const shouldRefreshCustomerId =
          !values.stripeCustomerId ||
          values.stripeCustomerId.startsWith('cd_') ||
          cardDetails.customerId !== values.stripeCustomerId;
        if (shouldRefreshCustomerId) {
          // if we didn't have a customer id or we created a new card with a chargedesk id, then we need to set the one we got back
          setFieldValue('stripeCustomerId', cardDetails.customerId);
          setFieldTouched('stripeCustomerId', true);
        }
      }
      if (cancelled) {
        setDisabled(true);
      }
    } catch (error) {
      toast.notify({
        type: 'error',
        message: `An error occurred. ${error.message.replace(':', '').trim()}`
      });
    }
  };

  const toggleAddCreditCardForm = () => {
    setShowAdd(!showAdd);
  };

  const toggleSendLinksModal = () => {
    setOpenCreditCardModal(!openCreditCardModal);
    setShowAdd(false);
  };

  const creditCardsOptions = allPaymentMethods
    .filter((method) => !!method.brand)
    .map((card) => ({
      id: card.id || '',
      value: `${card.brand ? card.brand : ''} ${maskingDots}${card.last4}`
    }));

  return (
    <>
      <Grid container className={classes.billingDetailsContainer} spacing={4} alignItems="center">
        <Grid item xs={4}>
          <LabelTooltip
            label="Primary Card Number"
            tooltip={{
              label: 'More Info',
              onHoverText: 'This is the primary card we’ll use to process payments for this policy.'
            }}
          >
            <FormField
              name="defaultCreditCard.id"
              type="select"
              mode="dark"
              options={creditCardsOptions}
              disabled={!creditCardsOptions.length || isCancelledOrInCancellation}
              fast={false}
              permissions={{ isLicensedAction: false }}
            />
          </LabelTooltip>
        </Grid>
        <StripeCustomerIdField />

        {/* -- Buttons that trigger forms and modals below -- */}

        {/* This "Add new card" shows for all staff users except view only users and allows users to send a link to add a new card or manually add a card */}
        <Button
          onClick={toggleSendLinksModal}
          variant="contained"
          color="secondary"
          className={classes.addCardButton}
          disabled={viewOnly}
        >
          <img src={addCard} alt="Add card" className={classes.addCardImage} />
          Add new card
        </Button>
      </Grid>

      {/* CC Form and modal that open/close based on buttons above */}
      {showAdd && isAgency && (
        <Grid container className={classes.billingDetailsContainer}>
          {showAdd && (
            <CreditCardForm
              onSubmit={handleAddCard}
              close={toggleAddCreditCardForm}
              buttonText="Add Card"
              showCloseButton
            />
          )}
        </Grid>
      )}
      {openCreditCardModal && (
        <SendCreditCardLinksModal
          open={openCreditCardModal}
          onClose={toggleSendLinksModal}
          onAddCard={handleAddCard}
          defaultPage={ccModalPage}
          isCancelledOrInCancellation={isCancelledOrInCancellation}
        />
      )}
      <Grid container className={classes.billingDetailsContainer} spacing={4}>
        {!viewOnly && <SecondaryPaymentMethod />}
      </Grid>
    </>
  );
};

CreditCards.propTypes = {
  ccModalPage: PropTypes.number.isRequired
};

export default observer(CreditCards);
